import { Link } from "react-router-dom";
import { Heart, Briefcase, Sparkles, Scale, Sprout, Download } from "lucide-react";
import SignaturesBlock from "../components/SignaturesBlock";

const PILLARS = [
  {
    icon: Heart,
    label: "Pilier 01",
    title: "Accès au cannabis médical",
    essence: "Des traitements sûrs et naturels pour les patients mauriciens.",
    bullets: [
      "Soulage douleurs chroniques, inflammations et anxiété.",
      "Alternative thérapeutique efficace, sans effets psychotropes (CBD).",
      "Améliore la qualité de vie des patients aujourd'hui privés d'accès.",
    ],
    case_study: { country: "Israël", fact: "Pionnier mondial : des milliers de patients suivis depuis 1992." },
    image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    accent: "#4A7A3D",
  },
  {
    icon: Briefcase,
    label: "Pilier 02",
    title: "Retombées économiques",
    essence: "Emplois, recettes fiscales, attractivité internationale.",
    bullets: [
      "Création de milliers d'emplois directs et indirects.",
      "Recettes fiscales substantielles pour l'État mauricien.",
      "Attractivité accrue pour les investisseurs étrangers.",
    ],
    case_study: { country: "Canada", fact: "18 milliards CAD de recettes cumulées en 7 ans." },
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    accent: "#C4944A",
  },
  {
    icon: Sparkles,
    label: "Pilier 03",
    title: "Considérations spirituelles",
    essence: "Respect des croyances et dialogue interculturel.",
    bullets: [
      "Reconnaissance culturelle des communautés rastafari.",
      "Cannabis utilisé dans des cérémonies spirituelles sacrées.",
      "Ouverture d'un dialogue intercommunautaire apaisé.",
    ],
    case_study: { country: "Jamaïque", fact: "Exemption religieuse rastafari reconnue depuis 2015." },
    image: "https://images.unsplash.com/photo-1504370805625-d32c54b16100?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    accent: "#8FAF82",
  },
  {
    icon: Scale,
    label: "Pilier 04",
    title: "Droits humains",
    essence: "Libertés individuelles et fin de la criminalisation disproportionnée.",
    bullets: [
      "Respect des libertés individuelles et de la dignité humaine.",
      "Fin des injustices liées à la criminalisation disproportionnée.",
      "Réduction du marché noir et accès à des produits contrôlés.",
    ],
    case_study: { country: "Afrique du Sud", fact: "La Cour constitutionnelle a invoqué la dignité humaine en 2018." },
    image: "https://images.unsplash.com/photo-1591025207163-942350e47db2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    accent: "#1A1A18",
  },
  {
    icon: Sprout,
    label: "Pilier 05",
    title: "Usage récréatif",
    essence: "Une légalisation encadrée vaut mieux qu'une prohibition aveugle.",
    bullets: [
      "Cadre légal de consommation récréative contrôlée (21+).",
      "Réduction des arrestations et recettes fiscales importantes.",
      "Contrôle qualité des produits, baisse des nuisances publiques.",
    ],
    case_study: { country: "Colorado", fact: "Recul des arrestations, recettes fiscales records, consommation stable." },
    image: "https://images.unsplash.com/photo-1532074205216-d0e1f4b87368?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    accent: "#4A7A3D",
  },
];

const TIMELINE = [
  { date: "17 octobre 2024", text: "Naissance officielle du collectif Rwana Bizin Lega." },
  { date: "Novembre 2024", text: "Première publication du plaidoyer en français et créole." },
  { date: "Mars 2025", text: "Le NADC constitue son groupe technique cannabis." },
  { date: "Juillet 2025", text: "Couverture internationale de Newsweed et Vice." },
  { date: "2026", text: "Mobilisation pour une consultation publique large." },
];

export default function Plaidoyer() {
  return (
    <div data-testid="plaidoyer-page">
      {/* Hero */}
      <section className="border-b-2 border-[#1A1A18] bg-[#1A1A18] text-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="font-mono-label text-[#C4944A] mb-5">Manifeste · 17 octobre 2024</div>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] uppercase">
              Rwana Bizin<br /><span className="text-[#C4944A]">Lega.</span>
            </h1>
            <p className="font-quote text-2xl md:text-3xl text-[#FAF8F3]/80 mt-8 max-w-2xl leading-snug">
              « Légalisation du cannabis à l'île Maurice. Un plaidoyer fondé sur la science, le droit et la dignité humaine. »
            </p>
          </div>
          <div className="md:col-span-4 space-y-3">
            <a
              href="https://customer-assets.emergentagent.com/job_bizin-lega/artifacts/b00411n6_RWANA_BIZIN_LEGA_Plaidoyer_VF.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#C4944A] text-[#1A1A18] px-6 py-4 font-mono-label flex items-center justify-between gap-3 hover:bg-[#FAF8F3]"
              data-testid="plaidoyer-download"
            >
              Télécharger le PDF complet <Download size={16} />
            </a>
            <Link to="/a-propos" className="border-2 border-[#FAF8F3]/40 px-6 py-4 font-mono-label flex items-center justify-between gap-3 hover:border-[#FAF8F3]" data-testid="plaidoyer-about-link">
              Qui sommes-nous ? →
            </Link>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="border-b border-[#E0D8CE]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="font-mono-label text-[#4A7A3D] mb-3">Introduction</div>
          <h2 className="font-display text-3xl md:text-5xl leading-tight mb-6">Un mouvement pluriel, une seule conviction.</h2>
          <p className="text-lg leading-relaxed text-[#1A1A18]">
            Rwana Bizin Lega rassemble experts en politique des drogues, travailleurs sociaux, économistes, juristes, scientifiques, médecins, intellectuels, artistes et usagers de cannabis. Né le 17 octobre 2024, le collectif réclame une légalisation encadrée du cannabis à l'île Maurice — fondée sur les données scientifiques, les meilleures pratiques internationales et les droits humains.
          </p>
          <p className="font-quote text-2xl text-[#4A7A3D] mt-8 leading-snug">
            « Ni diabolisation. Ni banalisation. Information. »
          </p>
        </div>
      </section>

      {/* Pillars — graphic grid (style "Explorer le territoire") */}
      <section className="border-b border-[#E0D8CE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="font-mono-label text-[#4A7A3D] mb-3">Les 5 piliers</div>
          <h2 className="font-display text-4xl md:text-6xl leading-tight mb-12 max-w-3xl">Cinq raisons de réformer.</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 border border-[#E0D8CE]">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <article
                  key={i}
                  className="p-6 md:p-7 border-r border-b border-[#E0D8CE] card-shift group relative bg-[#FAF8F3] flex flex-col"
                  data-testid={`plaidoyer-pillar-${i + 1}`}
                >
                  {/* Top accent bar */}
                  <span className="absolute top-0 left-0 w-full h-1.5" style={{ backgroundColor: p.accent }} />

                  {/* Header: label + number */}
                  <div className="flex items-start justify-between gap-3 mb-5 mt-1">
                    <div>
                      <div className="font-mono-label text-[#4A7A3D]">{String(i + 1).padStart(2, "0")} / {p.label.replace("Pilier ", "")}</div>
                      <Icon size={22} strokeWidth={1.7} className="text-[#1A1A18] mt-3" />
                    </div>
                    <div className="font-display text-6xl md:text-7xl leading-[0.85] text-[#1A1A18] opacity-15 group-hover:opacity-100 group-hover:text-[#4A7A3D] transition-all" style={{ color: p.accent }}>
                      0{i + 1}
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="font-display text-2xl md:text-3xl leading-[1.05] text-[#1A1A18] group-hover:text-[#4A7A3D] mb-4">
                    {p.title}
                  </h3>

                  {/* Essence */}
                  <p className="font-quote italic text-[#4B4B48] text-base leading-snug mb-4">
                    « {p.essence} »
                  </p>

                  {/* Bullets */}
                  <ul className="space-y-2 text-sm leading-snug flex-1">
                    {p.bullets.map((b, j) => (
                      <li key={j} className="flex gap-2">
                        <span className="font-display text-[#C4944A] flex-shrink-0">→</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Case study footer */}
                  <div className="mt-5 pt-4 border-t border-[#E0D8CE]">
                    <div className="font-mono-label text-[#4A7A3D] mb-1">CAS · {p.case_study.country}</div>
                    <p className="text-xs text-[#4B4B48] leading-snug">{p.case_study.fact}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="border-b border-[#E0D8CE]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="font-mono-label text-[#4A7A3D] mb-3">Chronologie</div>
          <h2 className="font-display text-4xl md:text-5xl mb-12">Un mouvement en marche.</h2>
          <div className="space-y-0 border-l-2 border-[#1A1A18]">
            {TIMELINE.map((t, i) => (
              <div key={i} className="relative pl-8 pb-10" data-testid={`plaidoyer-timeline-${i}`}>
                <div className="absolute -left-[7px] top-1 w-3 h-3 bg-[#C4944A] border-2 border-[#1A1A18]" />
                <div className="font-mono-label text-[#4A7A3D]">{t.date}</div>
                <p className="font-display text-xl md:text-2xl mt-1 leading-tight">{t.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Signataires */}
      <SignaturesBlock />

      {/* CTA */}
      <section className="bg-[#4A7A3D] text-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center">
          <h2 className="font-display text-4xl md:text-6xl leading-tight max-w-3xl mx-auto">Rejoindre le mouvement.</h2>
          <p className="text-lg mt-5 max-w-xl mx-auto text-[#FAF8F3]/85">
            Lis, partage, débats. Le changement passe d'abord par la connaissance.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="bg-[#FAF8F3] text-[#1A1A18] px-8 py-4 font-mono-label hover:bg-[#C4944A]" data-testid="plaidoyer-cta-contact">
              Nous contacter
            </Link>
            <Link to="/" className="border-2 border-[#FAF8F3] px-8 py-4 font-mono-label hover:bg-[#FAF8F3] hover:text-[#1A1A18]" data-testid="plaidoyer-cta-articles">
              Lire les articles
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
